import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, BrainCircuit, Loader2, Radar, RefreshCw, Sparkles } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from 'recharts';
import { toast } from 'sonner';

import AIActivityTimeline from '../components/ai/AIActivityTimeline';
import AIAnomalyTable from '../components/ai/AIAnomalyTable';
import AICopilotWorkbench from '../components/ai/AICopilotWorkbench';
import AIInsightCard from '../components/ai/AIInsightCard';
import AIPredictiveTrendWidget from '../components/ai/AIPredictiveTrendWidget';
import AIRecommendationPanel from '../components/ai/AIRecommendationPanel';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { formatCurrencyAmount } from '../lib/currency';
import { getErrorMessage } from '../lib/utils';
import { aiCommandAPI } from '../services/api';

const CARD_TONE = {
  primary: 'from-primary/10 via-card to-info/10',
  info: 'from-info/10 via-card to-card',
  success: 'from-emerald-50 via-card to-card',
  warning: 'from-amber-50 via-card to-card',
  danger: 'from-rose-50 via-card to-card',
};

const CHART_COLORS = ['#22d3ee', '#38bdf8', '#818cf8', '#10b981', '#f59e0b', '#f43f5e'];
const PANEL_CLASS = 'rounded-3xl border border-border bg-card text-foreground shadow-sm';
const SUB_PANEL_CLASS = 'rounded-2xl border border-border bg-muted';
const LABEL_CLASS = 'text-[11px] uppercase tracking-[0.18em] text-muted-foreground';

const TenantAICommandCenterPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    page_title: 'AI Dashboard',
    workforce_overview: [],
    ai_insights: [],
    copilot: {},
    talent_intelligence: {},
    performance_intelligence: {},
    payroll_intelligence: {},
    engagement_engine: {},
    forecasting: {},
    recommendations: [],
    anomalies: [],
    activity_timeline: [],
    decision_logs: [],
    compliance_snapshot: {},
    microservices: [],
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await aiCommandAPI.getTenantCommandCenter();
      setData(response.data || {});
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load AI tenant command center'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCopilotQuery = async (query) => {
    const response = await aiCommandAPI.queryCopilot(query);
    return response.data || {};
  };

  const moodData = useMemo(() => data.engagement_engine?.mood_analytics || [], [data.engagement_engine]);
  const overviewLookup = useMemo(
    () => Object.fromEntries((data.workforce_overview || []).map((item) => [item.key, item])),
    [data.workforce_overview]
  );

  if (loading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-3xl border border-border bg-card text-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading tenant AI Dashboard...
      </div>
    );
  }

  return (
    <div className="space-y-6 text-foreground">
      <section className="group relative overflow-hidden rounded-[28px] border border-border bg-gradient-to-br from-primary/10 via-card to-info/10 p-6 shadow-[0_22px_60px_rgba(15,23,42,0.08)]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-16 top-0 h-40 w-40 rounded-full bg-primary/10 blur-3xl transition duration-700 group-hover:scale-110" />
          <div className="absolute right-0 top-10 h-44 w-44 rounded-full bg-info/10 blur-3xl transition duration-700 group-hover:scale-110" />
        </div>
        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/80 px-3 py-1 text-xs font-medium text-primary shadow-sm backdrop-blur-sm">
              <BrainCircuit className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-12" />
              Organization Intelligence Layer
            </div>
            <div>
              <h1 className="text-hr-h1 font-bold tracking-tight text-foreground">{data.page_title || 'AI Dashboard'}</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
                Workforce optimization, HR intelligence, hiring analytics, payroll risk detection, engagement monitoring, and predictive recommendations from one operating view.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {(data.workforce_overview || []).map((item) => (
                <div key={item.key} className={`rounded-2xl border border-border bg-gradient-to-br ${CARD_TONE[item.tone] || CARD_TONE.primary} p-4 shadow-sm`}>
                  <p className={LABEL_CLASS}>{item.label}</p>
                  <p className="mt-3 text-2xl font-semibold text-foreground">{item.value}{item.key === 'attrition' ? '%' : ''}</p>
                  <p className="mt-2 text-xs text-muted-foreground">{item.delta}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-4">
            <div className="rounded-2xl border border-border bg-card/92 p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Compliance Snapshot</p>
                  <p className="mt-2 text-lg font-semibold text-foreground">AI risk and governance remain in one tenant-scoped control view.</p>
                </div>
                <Radar className="h-5 w-5 text-primary" />
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className={`${SUB_PANEL_CLASS} p-3`}>
                  <p className={LABEL_CLASS}>AI Risk</p>
                  <p className="mt-2 text-xl font-semibold text-foreground">{data.compliance_snapshot?.ai_risk_score}</p>
                </div>
                <div className={`${SUB_PANEL_CLASS} p-3`}>
                  <p className={LABEL_CLASS}>Compliance Health</p>
                  <p className="mt-2 text-xl font-semibold text-foreground">{data.compliance_snapshot?.compliance_health}</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3 xl:justify-end">
              <Button variant="outline" className="border-border bg-card/92 text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:bg-primary/10 hover:text-primary" onClick={loadData}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="grid gap-6">
          <AICopilotWorkbench config={data.copilot} onSubmit={handleCopilotQuery} variant="light" />

          <Card className={PANEL_CLASS}>
            <CardHeader>
              <CardTitle className="text-foreground">AI Workforce Insights</CardTitle>
              <CardDescription className="text-muted-foreground">Live organization-level intelligence signals.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 xl:grid-cols-2">
              {(data.ai_insights || []).map((insight) => (
                <AIInsightCard key={insight.id} insight={insight} variant="light" />
              ))}
            </CardContent>
          </Card>

          <div className="grid gap-6 xl:grid-cols-2">
            <Card className={PANEL_CLASS}>
              <CardHeader>
                <CardTitle className="text-foreground">AI Talent Intelligence</CardTitle>
                <CardDescription className="text-muted-foreground">Hiring funnel analytics, scoring, and bottlenecks.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.talent_intelligence?.hiring_funnel || []}>
                      <CartesianGrid stroke="rgba(148,163,184,0.2)" vertical={false} />
                      <XAxis dataKey="stage" stroke="#64748b" tickLine={false} axisLine={false} />
                      <YAxis stroke="#64748b" tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid rgba(226,232,240,1)', borderRadius: '14px', color: '#0f172a' }} />
                      <Bar dataKey="value" radius={[8, 8, 0, 0]} fill="#22d3ee" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-3">
                  {(data.talent_intelligence?.candidate_scoring || []).map((row) => (
                    <div key={row.role} className={`${SUB_PANEL_CLASS} p-4`}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-foreground">{row.role}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{row.recommendation}</p>
                        </div>
                        <div className="text-right">
                          <p className={LABEL_CLASS}>Score</p>
                          <p className="mt-1 text-lg font-semibold text-foreground">{row.score}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className={PANEL_CLASS}>
              <CardHeader>
                <CardTitle className="text-foreground">AI Performance Intelligence</CardTitle>
                <CardDescription className="text-muted-foreground">KPI trends, team matrix, skill gaps, and learning actions.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.performance_intelligence?.team_performance_matrix || []}>
                      <CartesianGrid stroke="rgba(148,163,184,0.2)" vertical={false} />
                      <XAxis dataKey="team" stroke="#64748b" tickLine={false} axisLine={false} />
                      <YAxis stroke="#64748b" tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid rgba(226,232,240,1)', borderRadius: '14px', color: '#0f172a' }} />
                      <Bar dataKey="performance" fill="#818cf8" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="engagement" fill="#22d3ee" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {(data.performance_intelligence?.skill_gaps || []).map((row) => (
                    <div key={row.skill} className={`${SUB_PANEL_CLASS} p-4`}>
                      <p className={LABEL_CLASS}>{row.skill}</p>
                      <p className="mt-2 text-xl font-semibold text-foreground">{row.gap_score}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="grid gap-6">
          <Card className={PANEL_CLASS}>
            <CardHeader>
              <CardTitle className="text-foreground">AI Payroll Intelligence</CardTitle>
              <CardDescription className="text-muted-foreground">Payroll anomaly detection, benchmarking, and cost forecasting.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className={`${SUB_PANEL_CLASS} p-4`}>
                  <p className={LABEL_CLASS}>Payroll Volume</p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">{formatCurrencyAmount(data.payroll_intelligence?.payroll_volume || 0, 'INR')}</p>
                </div>
                <div className={`${SUB_PANEL_CLASS} p-4`}>
                  <p className={LABEL_CLASS}>Anomaly Count</p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">{data.payroll_intelligence?.anomaly_count || 0}</p>
                </div>
              </div>
              <div className="space-y-3">
                {(data.payroll_intelligence?.overtime_anomalies || []).map((row) => (
                  <div key={row.department} className={`${SUB_PANEL_CLASS} p-4`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{row.department}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{row.hours} overtime hours</p>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${row.risk === 'high' ? 'bg-rose-500/10 text-rose-300' : 'bg-amber-500/10 text-amber-300'}`}>
                        {row.risk}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className={PANEL_CLASS}>
            <CardHeader>
              <CardTitle className="text-foreground">AI Engagement Engine</CardTitle>
              <CardDescription className="text-muted-foreground">Burnout prediction, mood analytics, and wellness signal.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={moodData} dataKey="value" nameKey="label" innerRadius={55} outerRadius={90} paddingAngle={4}>
                      {moodData.map((entry, index) => (
                        <Cell key={entry.label} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid rgba(226,232,240,1)', borderRadius: '14px', color: '#0f172a' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {(data.engagement_engine?.burnout_prediction || []).map((row) => (
                  <div key={row.department} className={`${SUB_PANEL_CLASS} p-4`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{row.department}</p>
                        <p className="mt-1 text-xs text-muted-foreground">Burnout prediction</p>
                      </div>
                      <p className="text-lg font-semibold text-foreground">{row.value}</p>
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-border">
                      <div className="h-2 rounded-full bg-gradient-to-r from-cyan-400 via-indigo-400 to-rose-400" style={{ width: `${Math.min(row.value, 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-4">
        <AIPredictiveTrendWidget title="Attrition Forecast" description="Predicted retention pressure" data={data.forecasting?.attrition || []} color="#f43f5e" suffix="%" variant="light" />
        <AIPredictiveTrendWidget title="Workforce Forecast" description="Projected active workforce" data={data.forecasting?.workforce || []} color="#22d3ee" variant="light" />
        <AIPredictiveTrendWidget title="Hiring Demand" description="Planned hiring pressure" data={data.forecasting?.hiring_demand || []} color="#818cf8" variant="light" />
        <AIPredictiveTrendWidget title="Productivity" description="Modeled productivity trend" data={data.forecasting?.productivity || []} color="#10b981" variant="light" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
        <Card className={PANEL_CLASS}>
          <CardHeader>
            <CardTitle className="text-foreground">AI Recommendation Engine</CardTitle>
            <CardDescription className="text-muted-foreground">Human-in-the-loop actions for leaders and HR operators.</CardDescription>
          </CardHeader>
          <CardContent>
            <AIRecommendationPanel rows={data.recommendations || []} variant="light" />
          </CardContent>
        </Card>

        <Card className={PANEL_CLASS}>
          <CardHeader>
            <CardTitle className="text-foreground">AI Monitoring Stream</CardTitle>
            <CardDescription className="text-muted-foreground">Live anomaly and operational risk feed.</CardDescription>
          </CardHeader>
          <CardContent>
            <AIAnomalyTable rows={data.anomalies || []} variant="light" />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <Card className={PANEL_CLASS}>
          <CardHeader>
            <CardTitle className="text-foreground">AI Activity Timeline</CardTitle>
            <CardDescription className="text-muted-foreground">Copilot runs, activity streams, and intelligence operations.</CardDescription>
          </CardHeader>
          <CardContent>
            <AIActivityTimeline rows={data.activity_timeline || []} variant="light" />
          </CardContent>
        </Card>

        <Card className={PANEL_CLASS}>
          <CardHeader>
            <CardTitle className="text-foreground">AI Decision Logs</CardTitle>
            <CardDescription className="text-muted-foreground">Governance traces and explainable policy decisions.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data.decision_logs || []).map((row) => (
              <div key={row.id} className={`${SUB_PANEL_CLASS} p-4`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{row.policy_name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{row.event_type} • {row.actor_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-300">{row.decision_status}</p>
                    <p className="mt-2 text-xs text-muted-foreground">{row.confidence_score}% confidence</p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className={PANEL_CLASS}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Activity className="h-4 w-4 text-primary" />
            AI Microservices
          </CardTitle>
          <CardDescription className="text-muted-foreground">Tenant-scoped AI service fabric and operating responsibilities.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 xl:grid-cols-3">
          {(data.microservices || []).map((service) => (
            <div key={service.name} className={`${SUB_PANEL_CLASS} p-4`}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-foreground">{service.name}</p>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${service.status === 'healthy' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-amber-500/10 text-amber-300'}`}>
                  {service.status}
                </span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{service.runtime} • {service.mode}</p>
              <p className="mt-3 text-sm text-muted-foreground">{service.purpose}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantAICommandCenterPage;
